import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {AiPromptBoxTemplatesButtonProps} from './ai-prompt-box-templates-button.types';
import styles from './ai-prompt-box-templates-button.module.scss';
import {ClickableDiv} from '@Components/clickable-div';
import {Text} from '@Components/text';
import {getAssetUrl} from '@Utils/s3.util';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AiPromptBoxSize} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxTemplatesTooltip} from './components/ai-prompt-box-templates-tooltip/ai-prompt-box-templates-tooltip';
import {hasUserSeenAiPromptBoxTemplatesTooltip, markAiPromptBoxTemplatesTooltipAsSeen} from './ai-prompt-box-templates-button.library';
import {openAttachTemplateGalleryDialog} from '@Libraries/ai-chat.library';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';

const sizeClassMap: Record<AiPromptBoxSize, string> = {
  [AiPromptBoxSize.SMALL]: styles.small,
  [AiPromptBoxSize.MEDIUM]: styles.medium,
  [AiPromptBoxSize.SINGLE_LINE]: styles.small,
  [AiPromptBoxSize.SMALL_EXTENDED]: styles.small,
};

export function AiPromptBoxTemplatesButton({searchTerms, promptBoxId}: AiPromptBoxTemplatesButtonProps): ReactElement {
  const {state} = useAiPromptBox(promptBoxId);
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MEDIUM);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasUserSeenTooltip, setHasUserSeenTooltip] = useState(true);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const check = async (): Promise<void> => {
      try {
        const hasSeen = await hasUserSeenAiPromptBoxTemplatesTooltip();
        setHasUserSeenTooltip(hasSeen);
      } catch (error) {
        console.error(error);
      }
    };
    void check();
  }, []);

  const handleClick = (): void => {
    openAttachTemplateGalleryDialog(searchTerms);
  };

  const handleMouseEnter = (): void => {
    if (!hasUserSeenTooltip) {
      setShowTooltip(true);
      setHasUserSeenTooltip(true);
      void markAiPromptBoxTemplatesTooltipAsSeen();
    }
  };

  const handleMouseLeave = (): void => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  };

  const closeTooltip = (): void => {
    setShowTooltip(false);
  };

  const getTooltip = (): ReactElement | null => {
    if (isMobile || !showTooltip) {
      return null;
    }

    return <AiPromptBoxTemplatesTooltip referenceElementRef={buttonRef} onClose={closeTooltip} />;
  };

  const getButtonText = (): ReactElement | null => {
    if (isMobile) {
      return null;
    }
    return <Text className={styles.buttonText} val={window.i18next.t('pmwjs_templates_capitalized')} />;
  };

  const getButton = (): ReactElement => {
    return (
      <ClickableDiv
        ref={buttonRef}
        className={`${styles.templatesButton} ${sizeClassMap[state.size]}`}
        onClick={handleClick}
        aria-label={isMobile ? window.i18next.t('pmwjs_templates_capitalized') : undefined}
      >
        <img className={styles.buttonImage} src={getAssetUrl('ai-prompt-box-templates.webp')} alt="" />
        {getButtonText()}
      </ClickableDiv>
    );
  };

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={styles.tooltipWrapper}>
      {getButton()}
      {getTooltip()}
    </div>
  );
}
